import { useEffect, useMemo, useRef, useState } from 'react';
import Quiz from '../Quiz';
import DynamicIcon from '../../../components/ui/DynamicIcon';
import PlayModePicker from '../../../components/quiz/PlayModePicker';
import QrcAudit from '../../../components/quiz/QrcAudit';
import useQuizStore from '../../../store/quizStore';
import api from '../../../services/api';
import useFeaturePing from '../../../hooks/useFeaturePing';
import Illustration from '../../../components/ui/Illustration';
import illuExpress from '../../../assets/illustrations/express.svg?raw';
import illuStandard from '../../../assets/illustrations/standard.svg?raw';
import illuMarathon from '../../../assets/illustrations/marathon.svg?raw';
import illuSurprise from '../../../assets/illustrations/surprise.svg?raw';
import { X, AlertTriangle, Play, Trash2, Shuffle, Lock } from 'lucide-react';

// Styles partagés de la refonte "synthèse" du Mélange.
const EX_SECTION_HEAD = { fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '0.95em', color: 'var(--heading)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 };
const PREVIEW_ROW = { display: 'flex', alignItems: 'center', gap: 8 };
const PREVIEW_LINE = { flex: 1, height: 1, background: 'rgba(255,255,255,0.2)' };
// Options du lancement personnalisé, en mode classique (migration 261) : temps max (minutes) et objectif (%).
// Mémorisées dans ce navigateur, par confort ; aucune n'est imposée.
// Raccourcis ; toute valeur entière dans les bornes peut aussi être saisie (ex. 180 min pour une épreuve de 3 h).
const TIME_OPTIONS = [30, 60, 90, 120, 180];
const TARGET_OPTIONS = [50, 60, 70, 75, 80];
const TIME_MAX = 480; // 8 h : garde-fou contre les fautes de frappe, pas un format d'épreuve (même borne que session.php)
const clampInt = (v, min, max) => { const n = Math.round(Number(v)); return Number.isFinite(n) && n >= min && n <= max ? n : null; };
// Mémorisées PAR THÉMATIQUE : un réglage ISTQB n'a rien à faire sur une thématique concours.
const OPTIONS_KEY = 'mq_melange_options';
const optionsKey = (scopeId) => `${OPTIONS_KEY}:${scopeId || 0}`;
const minutesLabel = (m) => (m < 60 ? `${m} min` : `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60}` : ''}`);
const readOptions = (scopeId) => {
    try { const o = JSON.parse(localStorage.getItem(optionsKey(scopeId)) || '{}'); return { minutes: clampInt(o.minutes, 1, TIME_MAX), target: clampInt(o.target, 1, 100) }; }
    catch { return { minutes: null, target: null }; }
};

export default function TabExamen({
    scopeInfo, categories: allCategories, has_access, user,
    examenNbQuestions, setExamenNbQuestions,
    examenCategories, setExamenCategories,
    examenDiffMin, setExamenDiffMin,
    examenDiffMax, setExamenDiffMax,
    examenLoading, setExamenLoading,
    examenActive, setExamenActive,
    requireAccess,
    quizOverlayBg,
    onQuizDone,
    verdictAction,
    verdictUpsell,
    parcours = null,
}) {
    useFeaturePing('melange');
    // Sans abonnement (thématique éditoriale ou communautaire) : mélange ouvert, limité aux sujets
    // d'essai. Le serveur applique la même limite (LearningPool : demo_only) ; l'écran l'annonce.
    const demoMode = !has_access && !!user && scopeInfo?.content_source !== 'user';
    const categories = useMemo(() => (demoMode
        ? allCategories.map(c => ({ ...c, sous_sujets: (c.sous_sujets || []).filter(ss => ss.is_demo) })).filter(c => c.sous_sujets.length > 0)
        : allCategories), [allCategories, demoMode]);
    const fullQuestions = useMemo(() => allCategories.reduce((s, c) =>
        s + (c.sous_sujets || []).filter(ss => ss.custom_status !== 'OBSOLETE').reduce((s2, ss) => s2 + (ss.nb_questions || 0), 0), 0), [allCategories]);
    const canPlay = () => (demoMode ? true : requireAccess());
    const { startExamenMelange, resumeSession } = useQuizStore();
    const [showModePicker, setShowModePicker] = useState(false);
    // Mélange au programme (migration 210) : avec un concours choisi, tirage limité au programme de son QCM,
    // à parts égales entre ses rubriques officielles. Coché par défaut ; ignoré si des catégories sont cochées.
    const [auProgramme, setAuProgramme] = useState(true);
    const [pendingMelange, setPendingMelange] = useState([]);
    const [custOpen, setCustOpen] = useState(false);
    const [timeMinutes, setTimeMinutes] = useState(() => readOptions(scopeInfo?.id).minutes);
    const [targetPct, setTargetPct] = useState(() => readOptions(scopeInfo?.id).target);
    const optionsScopeRef = useRef(scopeInfo?.id);
    // Saisies libres : brouillon affiché tel que tapé (« 30 » en cours de « 300 » ne doit pas basculer sur le raccourci).
    const [timeDraft, setTimeDraft] = useState(() => (timeMinutes && !TIME_OPTIONS.includes(timeMinutes) ? String(timeMinutes) : ''));
    const [targetDraft, setTargetDraft] = useState(() => (targetPct && !TARGET_OPTIONS.includes(targetPct) ? String(targetPct) : ''));
    // Les options ne s'appliquent qu'au lancement personnalisé, pas aux démarrages rapides.
    // Démarrage rapide en attente du choix du mode : { n } (Express, Standard, Marathon, Surprise), sinon null.
    // Il ne touche PAS aux réglages de « Personnaliser » : annuler le choix du mode ne laisse aucune trace.
    const [quick, setQuick] = useState(null);
    const withOptions = !quick;
    // Saisie hors bornes : signalée et bloquante (sinon la séance partirait sans la limite affichée).
    const timeInvalid = timeDraft !== '' && clampInt(timeDraft, 1, TIME_MAX) === null;
    const targetInvalid = targetDraft !== '' && clampInt(targetDraft, 1, 100) === null;
    const optionsInvalid = timeInvalid || targetInvalid;
    // Préréglage de la thématique (migration 262) : format publié (ISTQB…), appliqué en un clic, toujours modifiable.
    const preset = scopeInfo?.melange_preset?.questions ? scopeInfo.melange_preset : null;
    const presetActive = !!preset && examenNbQuestions === preset.questions && examenCategories.length === 0
        && examenDiffMin === 1 && examenDiffMax === 3
        && (timeMinutes || null) === (preset.minutes || null) && (targetPct || null) === (preset.target_pct || null);
    const applyPreset = () => {
        if (!preset) return;
        setExamenNbQuestions(preset.questions);
        setExamenCategories([]);
        setExamenDiffMin(1); setExamenDiffMax(3);
        setTimeMinutes(preset.minutes || null); setTimeDraft(preset.minutes && !TIME_OPTIONS.includes(preset.minutes) ? String(preset.minutes) : '');
        setTargetPct(preset.target_pct || null); setTargetDraft(preset.target_pct && !TARGET_OPTIONS.includes(preset.target_pct) ? String(preset.target_pct) : '');
    };
    // Changement de thématique sans remontage : on recharge ses propres réglages avant toute écriture.
    useEffect(() => {
        if (optionsScopeRef.current === scopeInfo?.id) return;
        optionsScopeRef.current = scopeInfo?.id;
        const o = readOptions(scopeInfo?.id);
        setTimeMinutes(o.minutes); setTimeDraft(o.minutes && !TIME_OPTIONS.includes(o.minutes) ? String(o.minutes) : '');
        setTargetPct(o.target); setTargetDraft(o.target && !TARGET_OPTIONS.includes(o.target) ? String(o.target) : '');
    }, [scopeInfo?.id]);
    useEffect(() => {
        if (optionsScopeRef.current !== scopeInfo?.id) return;
        try { localStorage.setItem(optionsKey(scopeInfo?.id), JSON.stringify({ minutes: timeMinutes, target: targetPct })); } catch { /* stockage indisponible */ }
    }, [timeMinutes, targetPct]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch pending melange sessions (plusieurs tentatives transverses possibles)
    useEffect(() => {
        if (!scopeInfo?.id || !user) return;
        api.get(`/qcm/session.php?scope_id=${scopeInfo.id}&type=melange`)
            .then(({ data }) => setPendingMelange(data.data || []))
            .catch(e => console.warn('Erreur chargement session:', e));
    }, [scopeInfo?.id, user, examenActive]);

    const handleResumeMelange = async (session) => {
        const ok = await resumeSession(session.id, { scopeId: scopeInfo.id, isCustom: scopeInfo?.content_source === 'user', isPartner: scopeInfo?.content_source === 'partner' });
        if (ok) setExamenActive(true);
    };

    // Annuler une séance (29/10/2026) : seulement sans aucune réponse, revérifié par le serveur (only_if_empty) ;
    // s'il refuse (réponse enregistrée entre-temps), on relit les séances avec leur vrai compteur.
    const cancelMelangeSession = async (session) => {
        try {
            await api.delete('/qcm/session.php', { data: { session_id: session.id, only_if_empty: true } });
            setPendingMelange(prev => prev.filter(s => s.id !== session.id));
        } catch (e) {
            console.warn('Erreur annulation session:', e);
            api.get(`/qcm/session.php?scope_id=${scopeInfo.id}&type=melange`)
                .then(({ data }) => setPendingMelange(data.data || []))
                .catch(() => {});
        }
    };

    // Compute available content stats
    const contentStats = useMemo(() => {
        // Exclure les sous-sujets OBSOLETE (non jouables) des stats d'examen mélange.
        const activeSs = (c) => (c.sous_sujets || []).filter(ss => ss.custom_status !== 'OBSOLETE');
        // Questions d'un sous-sujet DANS la plage de difficulté [dMin,dMax] (cf. nb_q1/2/3
        // exposés par detail.php ; mêmes bornes que le filtre BETWEEN de l'examen).
        const ssByDiff = (ss, dMin, dMax) => {
            let n = 0;
            if (dMin <= 1 && dMax >= 1) n += ss.nb_q1 || 0;
            if (dMin <= 2 && dMax >= 2) n += ss.nb_q2 || 0;
            if (dMin <= 3 && dMax >= 3) n += ss.nb_q3 || 0;
            return n;
        };
        const totalSousSujets = categories.reduce((s, c) => s + activeSs(c).length, 0);
        const totalQuestions = categories.reduce((s, c) =>
            s + activeSs(c).reduce((s2, ss) => s2 + (ss.nb_questions || 0), 0), 0);

        // Per-category stats : nbQuestions = total (tous niveaux, pour l'affichage carte) ;
        // nbByDiff = dans la plage de difficulté courante (pour le pool dispo).
        const catStats = categories.map(c => ({
            id: c.id,
            label: c.label,
            nbSousSujets: activeSs(c).length,
            nbQuestions: activeSs(c).reduce((s, ss) => s + (ss.nb_questions || 0), 0),
            nbByDiff: activeSs(c).reduce((s, ss) => s + ssByDiff(ss, examenDiffMin, examenDiffMax), 0),
        }));

        // Pool dispo = catégories sélectionnées (ou toutes) × plage de difficulté.
        const pool = examenCategories.length === 0 ? catStats : catStats.filter(c => examenCategories.includes(c.id));
        const selectedQuestions = pool.reduce((s, c) => s + c.nbByDiff, 0);

        return { totalSousSujets, totalQuestions, catStats, selectedQuestions };
    }, [categories, examenCategories, examenDiffMin, examenDiffMax]);

    const { totalSousSujets, totalQuestions, catStats, selectedQuestions } = contentStats;
    const hasEnoughContent = totalSousSujets >= 2 && totalQuestions >= 5;
    const maxAvailableQuestions = selectedQuestions;

    const toggleExamenCategorie = (catId) => {
        setExamenCategories(prev =>
            prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
        );
    };

    const onExamenLaunchClick = () => {
        if (!canPlay() || optionsInvalid) return;
        setQuick(null);
        setShowModePicker(true);
    };

    const handleStartExamen = async (playMode) => {
        setShowModePicker(false);
        setExamenLoading(true);
        const ok = await startExamenMelange(scopeInfo.id, {
            nbQuestions: quick ? Math.min(quick.n, totalQuestions) : Math.min(examenNbQuestions, maxAvailableQuestions),
            categories: quick ? [] : examenCategories,
            difficulteMin: quick ? 1 : examenDiffMin,
            difficulteMax: quick ? 3 : examenDiffMax,
            isCustom: scopeInfo?.content_source === 'user',
            isPartner: scopeInfo?.content_source === 'partner',
            playMode,
            parcours: parcours?.slug ?? null,
            programme: !!parcours && auProgramme && (!!quick || examenCategories.length === 0),
            ...(withOptions && timeMinutes ? { timeLimit: timeMinutes * 60 } : {}),
            ...(withOptions && targetPct ? { targetPct } : {}),
        });
        setExamenLoading(false);
        if (ok) setExamenActive(true);
    };

    // Démarrage rapide : preset = toutes catégories + toute difficulté + nb fixe → mode picker.
    const launchPreset = (n) => {
        if (!canPlay()) return;
        setQuick({ n });
        setShowModePicker(true);
    };
    // Surprise : nombre au hasard (parmi ceux que le contenu permet), tout le reste ouvert.
    const launchSurprise = () => {
        if (!canPlay()) return;
        const choices = [10, 20, 30, 40, 50].filter(n => n <= totalQuestions);
        setQuick({ n: choices.length ? choices[Math.floor(Math.random() * choices.length)] : 10 });
        setShowModePicker(true);
    };

    if (!user) return <p className="sl-login-prompt">Connectez-vous pour accéder à l'entraînement transversal.</p>;

    // Examen actif en plein ecran (croix gérée par Quiz via --fixed)
    if (examenActive) {
        return (
            <div className={`quiz-modal-overlay ${quizOverlayBg?.backgroundImage ? 'has-bg-image' : ''}`} style={quizOverlayBg}>
                <Quiz
                    scopeInfoProp={scopeInfo}
                    verdictAction={verdictAction}
                    verdictUpsell={verdictUpsell}
                    onClose={() => { useQuizStore.getState().reset(); setExamenActive(false); onQuizDone?.(); }}
                />
            </div>
        );
    }

    const DIFF_LABELS = { 1: 'Facile', 2: 'Moyen', 3: 'Difficile' };
    const NB_OPTIONS = [10, 20, 30, 40, 50];
    // Aperçu live de la sélection (carte de droite desktop / récap collant mobile).
    const previewCount = Math.min(examenNbQuestions, maxAvailableQuestions);
    const diffLabel = examenDiffMin === examenDiffMax ? DIFF_LABELS[examenDiffMin] : `${DIFF_LABELS[examenDiffMin]} → ${DIFF_LABELS[examenDiffMax]}`;
    const catLabel = examenCategories.length === 0 ? `Toutes (${catStats.length})` : `${examenCategories.length} / ${catStats.length}`;

    // Not enough content
    if (!hasEnoughContent && demoMode) {
        return (
            <div className="sl-examen-blanc">
                <div className="sl-examen-header">
                    <h3 className="sl-section-title">Quiz transversal</h3>
                </div>
                <div className="sl-examen-empty">
                    <Lock size={32} strokeWidth={1.5} />
                    <p><strong>Le mélange s’ouvre avec l’abonnement</strong></p>
                    <p>Il mélange des questions de plusieurs sujets ; les sujets d’essai de cette thématique n’en ont pas assez.</p>
                    <button type="button" className="btn btn-primary" onClick={() => requireAccess()}>Voir les offres</button>
                </div>
            </div>
        );
    }

    if (!hasEnoughContent) {
        return (
            <div className="sl-examen-blanc">
                <div className="sl-examen-header">
                    <h3 className="sl-section-title">Quiz transversal</h3>
                </div>
                <div className="sl-examen-empty">
                    <AlertTriangle size={32} strokeWidth={1.5} />
                    <p>
                        <strong>Contenu insuffisant pour un quiz transversal</strong>
                    </p>
                    <p>
                        Il faut au moins <strong>2 sujets</strong> et <strong>5 questions</strong> pour lancer un quiz mélangé.
                        <br />
                        Actuellement : {totalSousSujets} sujet{totalSousSujets > 1 ? 's' : ''} et {totalQuestions} question{totalQuestions > 1 ? 's' : ''}.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="sl-examen-blanc">
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                    <span style={{ width: 38, height: 38, borderRadius: 11, background: scopeInfo?.couleur_theme ? `linear-gradient(135deg, rgba(255,255,255,0.18), rgba(0,0,0,0.16)), ${scopeInfo.couleur_theme}` : 'linear-gradient(135deg,#0d9488,#0ea5a4)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Shuffle size={19} /></span>
                    <div>
                        <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '1.4em', color: 'var(--heading)', margin: 0, lineHeight: 1.1 }}>Quiz transversal</h1>
                        <div style={{ fontSize: '0.85em', color: 'var(--text-muted)' }}>Lance-toi en un clic, ou personnalise</div>
                    </div>
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.78em', color: 'var(--text-muted)', textAlign: 'right' }}>
                    <strong style={{ color: 'var(--heading)', fontFamily: 'var(--font-display)', fontSize: '1.2em' }}>{totalQuestions}</strong> questions · {catStats.length} catégories
                </div>
            </div>

            {demoMode && (
                <div className="melange-demo">
                    <Lock size={16} />
                    <p>
                        <strong>Version gratuite</strong> : le mélange pioche dans les {totalSousSujets} sujets d’essai, soit {totalQuestions} questions
                        {fullQuestions > 0 && <> sur {fullQuestions.toLocaleString('fr-FR')} ({totalQuestions * 100 < fullQuestions ? '< 1' : Math.round(totalQuestions / fullQuestions * 100)} % de la thématique)</>}.
                    </p>
                    <button type="button" className="btn btn-sm btn-primary" onClick={() => requireAccess()}>
                        Tout débloquer
                    </button>
                </div>
            )}

            {/* ===== DÉMARRAGE RAPIDE ===== */}
            <div style={{ marginBottom: 22 }}>
                <div style={EX_SECTION_HEAD}>⚡ Démarrage rapide</div>
                {parcours && (
                    <label className="melange-programme">
                        <input type="checkbox" checked={auProgramme} onChange={e => setAuProgramme(e.target.checked)} />
                        <span>
                            <strong>Au programme du QCM {parcours.label}</strong>
                            <small>Seulement les sujets du programme, à parts égales entre ses rubriques officielles.</small>
                        </span>
                    </label>
                )}
                <div className="melange-presets">
                    <button type="button" className="melange-preset" onClick={() => launchPreset(10)}>
                        <Illustration className="melange-preset__illu" svg={illuExpress} color={scopeInfo?.couleur_theme} />
                        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--heading)', marginTop: 8 }}>Express</div>
                        <div className="melange-preset__num">10</div>
                        <div style={{ fontSize: '0.78em', color: 'var(--text-muted)' }}>~3 min</div>
                    </button>
                    <button type="button" className="melange-preset" onClick={() => launchPreset(20)}>
                        <Illustration className="melange-preset__illu" svg={illuStandard} color={scopeInfo?.couleur_theme} />
                        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--heading)', marginTop: 8 }}>Standard</div>
                        <div className="melange-preset__num">20</div>
                        <div style={{ fontSize: '0.78em', color: 'var(--text-muted)' }}>~7 min</div>
                    </button>
                    <button type="button" className="melange-preset" onClick={() => launchPreset(50)}>
                        <Illustration className="melange-preset__illu" svg={illuMarathon} color={scopeInfo?.couleur_theme} />
                        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--heading)', marginTop: 8 }}>Marathon</div>
                        <div className="melange-preset__num">50</div>
                        <div style={{ fontSize: '0.78em', color: 'var(--text-muted)' }}>~18 min</div>
                    </button>
                    <button type="button" className="melange-preset melange-preset--surprise" onClick={launchSurprise}>
                        <Illustration className="melange-preset__illu" svg={illuSurprise} color={scopeInfo?.couleur_theme} />
                        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginTop: 8 }}>Surprise</div>
                        <div style={{ fontSize: '0.78em', opacity: 0.85, marginTop: 8 }}>Tout au hasard,<br />{parcours && auProgramme ? 'dans le programme' : 'toutes catégories'}</div>
                    </button>
                </div>
            </div>

            {/* ===== PERSONNALISER (tiroir : aperçu live desktop / récap collant mobile) ===== */}
            <div style={{ marginBottom: 22, background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 14, overflow: 'hidden' }}>
                <button type="button" onClick={() => setCustOpen(v => !v)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '16px 22px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', textAlign: 'left' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: '1.1em' }}>⚙️</span>
                        <div>
                            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.05em', color: 'var(--heading)' }}>Personnaliser</div>
                            <div style={{ fontSize: '0.82em', color: 'var(--text-muted)', marginTop: 2 }}>Nombre · difficulté · catégories précises · temps · objectif de score</div>
                        </div>
                    </div>
                    <span style={{ color: 'var(--primary)', fontWeight: 700, fontSize: '0.92em', flexShrink: 0 }}>{custOpen ? 'Replier ▴' : 'Déplier ▾'}</span>
                </button>
                {custOpen && (
                    <div style={{ padding: '2px 22px 22px' }}>
                        <div className="melange-cust">
                            <div className="sl-examen-config" style={{ margin: 0, padding: 0, background: 'transparent', border: 'none', boxShadow: 'none', borderRadius: 0 }}>
                                {/* Préréglage de la thématique (format publié, sourcé) */}
                                {preset && (
                                    <div className={`sl-examen-preset ${presetActive ? 'is-active' : ''}`}>
                                        <button type="button" className="sl-examen-preset__btn" onClick={applyPreset} aria-pressed={presetActive}>
                                            <strong>{presetActive ? '✓ ' : ''}Préréglage {preset.label}</strong>
                                            <span>
                                                {[`${preset.questions} questions`, preset.minutes ? minutesLabel(preset.minutes) : null, preset.target_pct ? `objectif ${preset.target_pct} %` : null].filter(Boolean).join(' · ')}
                                            </span>
                                        </button>
                                        <span className="sl-examen-preset__src">
                                            Format publié : {preset.source_url
                                                ? <a href={preset.source_url} target="_blank" rel="noopener noreferrer">{preset.source}</a>
                                                : preset.source}
                                            {preset.note ? `. ${preset.note}` : ''} Tout reste modifiable ci-dessous.
                                        </span>
                                    </div>
                                )}
                                {/* Nombre de questions */}
                                <div className="sl-examen-field">
                                    <label className="sl-examen-label">Nombre de questions</label>
                                    <div className="sl-examen-options">
                                        {NB_OPTIONS.map(n => (
                                            <button
                                                key={n}
                                                className={`sl-examen-option ${examenNbQuestions === n ? 'active' : ''}`}
                                                onClick={() => setExamenNbQuestions(n)}
                                                disabled={n > maxAvailableQuestions}
                                                title={n > maxAvailableQuestions ? `Seulement ${maxAvailableQuestions} questions disponibles` : ''}
                                            >
                                                {n}
                                            </button>
                                        ))}
                                    </div>
                                    {maxAvailableQuestions < 50 && (
                                        <span className="sl-examen-avail-note">
                                            {maxAvailableQuestions} question{maxAvailableQuestions > 1 ? 's' : ''} disponible{maxAvailableQuestions > 1 ? 's' : ''}
                                            {examenCategories.length > 0 ? ' dans les catégories sélectionnées' : ''}
                                        </span>
                                    )}
                                </div>

                                {/* Difficulté — 2 barres dégradées, 1 curseur chacune (Min / Max) */}
                                <div className="sl-examen-field">
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                                        <label className="sl-examen-label" style={{ margin: 0 }}>Difficulté</label>
                                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.78em', color: 'var(--primary)' }}>{diffLabel}</span>
                                    </div>
                                    {[
                                        { lbl: 'Min', val: examenDiffMin, onChange: (d) => { setExamenDiffMin(d); if (d > examenDiffMax) setExamenDiffMax(d); } },
                                        { lbl: 'Max', val: examenDiffMax, onChange: (d) => { setExamenDiffMax(d); if (d < examenDiffMin) setExamenDiffMin(d); } },
                                    ].map(({ lbl, val, onChange }) => (
                                        <div key={lbl} style={{ marginBottom: 10 }}>
                                            <div className="diff-dual__lbl">{lbl} : {DIFF_LABELS[val]}</div>
                                            <input type="range" className="diff-range" min={1} max={3} step={1} value={val}
                                                onChange={(e) => onChange(+e.target.value)} aria-label={`Difficulté ${lbl}`} />
                                        </div>
                                    ))}
                                    <div className="diff-bar__labels"><span>Facile</span><span>Moyen</span><span>Difficile</span></div>
                                </div>

                                {/* Categories */}
                                {categories.length > 1 && (
                                    <div className="sl-examen-field">
                                        <label className="sl-examen-label">
                                            Categories
                                            <span className="sl-examen-hint">
                                                {examenCategories.length === 0 ? '(toutes)' : `(${examenCategories.length} selectionnee${examenCategories.length > 1 ? 's' : ''})`}
                                            </span>
                                        </label>
                                        <div className="sl-examen-cats">
                                            {categories.map(cat => {
                                                const cs = catStats.find(c => c.id === cat.id);
                                                const isEmpty = !cs || cs.nbQuestions === 0;
                                                return (
                                                    <button
                                                        key={cat.id}
                                                        className={`sl-examen-cat ${cat.image ? 'sl-examen-cat--bg' : ''} ${examenCategories.includes(cat.id) ? 'active' : ''} ${isEmpty ? 'sl-examen-cat--empty' : ''}`}
                                                        onClick={() => !isEmpty && toggleExamenCategorie(cat.id)}
                                                        disabled={isEmpty}
                                                        title={isEmpty ? 'Aucune question dans cette catégorie' : `${cs.nbQuestions} questions`}
                                                        style={{
                                                            ...(cat.image ? { backgroundImage: `url(${cat.image})`, '--cat-color': cat.couleur_theme || 'var(--primary)' } : {}),
                                                            ...(!cat.image && examenCategories.includes(cat.id) && cat.couleur_theme ? { background: cat.couleur_theme, borderColor: cat.couleur_theme } : {}),
                                                        }}
                                                    >
                                                        <span>
                                                            {cat.icon && <DynamicIcon name={cat.icon} size={14} />}
                                                            {cat.label}
                                                            {cs && <small> ({cs.nbQuestions})</small>}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Temps max et objectif (mode classique) */}
                                <div className="sl-examen-field">
                                    <label className="sl-examen-label">
                                        Temps max
                                        <span className="sl-examen-hint">(facultatif, mode classique)</span>
                                    </label>
                                    <div className="sl-examen-options">
                                        <button className={`sl-examen-option ${!timeMinutes && !timeDraft ? 'active' : ''}`} onClick={() => { setTimeMinutes(null); setTimeDraft(''); }}>Libre</button>
                                        {TIME_OPTIONS.map(m => (
                                            <button key={m} className={`sl-examen-option ${!timeDraft && timeMinutes === m ? 'active' : ''}`} onClick={() => { setTimeMinutes(m); setTimeDraft(''); }}>
                                                {minutesLabel(m)}
                                            </button>
                                        ))}
                                        <label className={`sl-examen-option sl-examen-option--input ${timeDraft && timeMinutes ? 'active' : ''} ${timeInvalid ? 'is-invalid' : ''}`}>
                                            <input type="number" min={1} max={TIME_MAX} step={1} inputMode="numeric" placeholder="Autre"
                                                value={timeDraft}
                                                onChange={e => { setTimeDraft(e.target.value); setTimeMinutes(clampInt(e.target.value, 1, TIME_MAX)); }}
                                                aria-invalid={timeInvalid} aria-label="Temps max en minutes" /> min
                                        </label>
                                    </div>
                                    {timeInvalid && <span className="sl-examen-error" role="alert">Saisis une durée entre 1 et {TIME_MAX} minutes.</span>}
                                </div>
                                <div className="sl-examen-field">
                                    <label className="sl-examen-label">
                                        Objectif de score
                                        <span className="sl-examen-hint">(facultatif, mode classique)</span>
                                    </label>
                                    <div className="sl-examen-options">
                                        <button className={`sl-examen-option ${!targetPct && !targetDraft ? 'active' : ''}`} onClick={() => { setTargetPct(null); setTargetDraft(''); }}>Aucun</button>
                                        {TARGET_OPTIONS.map(p => (
                                            <button key={p} className={`sl-examen-option ${!targetDraft && targetPct === p ? 'active' : ''}`} onClick={() => { setTargetPct(p); setTargetDraft(''); }}>
                                                {p}{' '}%
                                            </button>
                                        ))}
                                        <label className={`sl-examen-option sl-examen-option--input ${targetDraft && targetPct ? 'active' : ''} ${targetInvalid ? 'is-invalid' : ''}`}>
                                            <input type="number" min={1} max={100} step={1} inputMode="numeric" placeholder="Autre"
                                                value={targetDraft}
                                                onChange={e => { setTargetDraft(e.target.value); setTargetPct(clampInt(e.target.value, 1, 100)); }}
                                                aria-invalid={targetInvalid} aria-label="Objectif de score en pourcentage" /> %
                                        </label>
                                    </div>
                                    {targetInvalid && <span className="sl-examen-error" role="alert">Saisis un objectif entre 1 et 100 %.</span>}
                                    {(timeMinutes || targetPct) && (
                                        <span className="sl-examen-avail-note">
                                            {timeMinutes ? 'À la fin du temps, tu choisis de valider ou de poursuivre. ' : ''}
                                            {targetPct ? 'Ton bilan indique si l’objectif est atteint.' : ''}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Aperçu live (desktop) */}
                            <div className="melange-cust__preview">
                                <div style={{ background: 'var(--gradient-brand)', borderRadius: 16, padding: 22, color: '#fff' }}>
                                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7em', letterSpacing: 1.5, color: 'rgba(255,255,255,0.8)' }}>TA SÉLECTION</div>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
                                        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '2.6em', lineHeight: 1 }}>{previewCount}</span>
                                        <span style={{ fontSize: '0.9em', opacity: 0.9 }}>questions</span>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 16, fontSize: '0.82em' }}>
                                        <div style={PREVIEW_ROW}><span style={{ opacity: 0.7 }}>Difficulté</span><span style={PREVIEW_LINE} /><b style={{ fontFamily: 'var(--font-display)' }}>{diffLabel}</b></div>
                                        <div style={PREVIEW_ROW}><span style={{ opacity: 0.7 }}>Catégories</span><span style={PREVIEW_LINE} /><b style={{ fontFamily: 'var(--font-display)' }}>{catLabel}</b></div>
                                        {timeMinutes && <div style={PREVIEW_ROW}><span style={{ opacity: 0.7 }}>Temps max</span><span style={PREVIEW_LINE} /><b style={{ fontFamily: 'var(--font-display)' }}>{minutesLabel(timeMinutes)}</b></div>}
                                        {targetPct && <div style={PREVIEW_ROW}><span style={{ opacity: 0.7 }}>Objectif</span><span style={PREVIEW_LINE} /><b style={{ fontFamily: 'var(--font-display)' }}>{targetPct}{' '}%</b></div>}
                                        <div style={PREVIEW_ROW}><span style={{ opacity: 0.7 }}>Pioche dans</span><span style={PREVIEW_LINE} /><b style={{ fontFamily: 'var(--font-display)' }}>{maxAvailableQuestions} questions</b></div>
                                    </div>
                                    <button onClick={onExamenLaunchClick} disabled={examenLoading || maxAvailableQuestions < 5 || optionsInvalid}
                                        style={{ width: '100%', marginTop: 18, padding: 13, borderRadius: 11, background: '#fff', color: '#6d28d9', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1em' }}>
                                        {examenLoading ? 'Génération…' : "Lancer l'entraînement ›"}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Récap collant (mobile) */}
                        <div className="melange-cust__recap">
                            <div style={{ fontSize: '0.85em', color: 'var(--text-muted)' }}>
                                <strong style={{ color: 'var(--heading)', fontFamily: 'var(--font-display)' }}>{previewCount}</strong> Q · {diffLabel} · {catLabel}
                                {timeMinutes ? ` · ⏱ ${minutesLabel(timeMinutes)}` : ''}{targetPct ? ` · 🎯 ${targetPct} %` : ''}
                            </div>
                            <button className="btn btn-primary btn-sm" onClick={onExamenLaunchClick} disabled={examenLoading || maxAvailableQuestions < 5 || optionsInvalid}>
                                {examenLoading ? '…' : "Lancer ›"}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ===== AUDIT RÉDIGÉ (QRC) — phase séparée, ne s'affiche que s'il y a des QRC ===== */}
            <QrcAudit scopeId={scopeInfo?.id} scopeColor={scopeInfo?.couleur_theme} />

            {/* ===== SESSIONS EN COURS ===== */}
            {pendingMelange.length > 0 && (
                <div className="sl-error-pending" style={{ marginBottom: 16 }}>
                    <h4 className="sl-error-pending-title">
                        Sessions en cours{pendingMelange.length > 1 ? ` (${pendingMelange.length})` : ''}
                    </h4>
                    {pendingMelange.map((s) => (
                        <div key={s.id} className="sl-error-pending-card" style={{ '--pick-color': '#6366f1' }}>
                            <span className="sl-error-pending-indicator" />
                            <div className="sl-error-pending-info">
                                <strong><Shuffle size={13} /> Quiz transversal</strong>
                                <span>{s.nb_answered}/{s.nb_questions} répondu(es){s.created_at ? ` · démarrée le ${new Date(s.created_at.replace(' ', 'T')).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}</span>
                            </div>
                            <button className="btn btn-primary btn-sm" onClick={() => handleResumeMelange(s)}>
                                <Play size={16} /> Reprendre
                            </button>
                            {s.nb_answered === 0 && (
                                <button
                                    className="btn btn-xs btn-ghost sl-error-pending-cancel"
                                    title="Annuler cette séance (aucune réponse)"
                                    onClick={() => cancelMelangeSession(s)}
                                >
                                    <Trash2 size={10} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Mode picker popup */}
            {showModePicker && (
                <PlayModePicker
                    title="Quiz transversal"
                    note={withOptions && (timeMinutes || targetPct)
                        ? `${[timeMinutes ? `Temps max ${minutesLabel(timeMinutes)}` : null, targetPct ? `objectif ${targetPct} %` : null].filter(Boolean).join(' · ')} : en mode Classique seulement (l’Apprentissage corrige au fil des questions, sans chrono ni objectif).`
                        : undefined}
                    onSelect={handleStartExamen}
                    onCancel={() => { setShowModePicker(false); setQuick(null); }}
                />
            )}

        </div>
    );
}
